import React, { useState, useEffect } from "react";
import {
  Popover,
  Box,
  IconButton,
  Typography,
  Button,
  Tooltip,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { operationsDashboardStyles } from "./styles";
import { colors } from "../../utils/colors";
import { months } from "../../utils/common";
import { MonthYearRangePickerProps } from "./type";
import { getDefaultDateRange } from "./utils";
import { filterFieldTooltipProps } from "../FilterDropdowns";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export default function MonthYearRangePicker({
  onChange,
  initialStartMonth = null,
  initialEndMonth = null,
}: MonthYearRangePickerProps) {
  const getInitialValues = () => {
    if (initialStartMonth && initialEndMonth) {
      return {
        startMonth: initialStartMonth,
        endMonth: initialEndMonth,
        year: initialStartMonth.year(),
      };
    }

    const { startDate, endDate } = getDefaultDateRange();
    return {
      startMonth: startDate,
      endMonth: endDate,
      year: endDate.year(),
    };
  };

  const initialValues = getInitialValues();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [year, setYear] = useState(initialValues.year);
  const [startMonth, setStartMonth] = useState<Dayjs | null>(
    initialValues.startMonth
  );
  const [endMonth, setEndMonth] = useState<Dayjs | null>(
    initialValues.endMonth
  );

  useEffect(() => {
    if (!initialStartMonth && !initialEndMonth && startMonth && endMonth) {
      onChange?.(startMonth, endMonth);
    }
  }, []);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);

  const handleMonthClick = (monthIndex: number) => {
    const selectedDate = dayjs().year(year).month(monthIndex).startOf("month");

    if (!startMonth || (startMonth && endMonth)) {
      setStartMonth(selectedDate);
      setEndMonth(null);
    } else if (selectedDate.isBefore(startMonth)) {
      setEndMonth(startMonth);
      setStartMonth(selectedDate);
      onChange?.(selectedDate, startMonth);
      handleClose();
    } else {
      setEndMonth(selectedDate);
      onChange?.(startMonth, selectedDate);
      handleClose();
    }
  };

  const formatRange = () => {
    if (!startMonth || !endMonth) {
      const { startDate, endDate } = getDefaultDateRange();
      return `${startDate.format("MMM YYYY")} - ${endDate.format("MMM YYYY")}`;
    }
    return `${startMonth.format("MMM YYYY")} - ${endMonth.format("MMM YYYY")}`;
  };

  const getFinancialYearBounds = () => {
    const today = dayjs();
    const lastMonth = today.subtract(1, "month").endOf("month");
    const currentFY = lastMonth.month() >= 3 ? lastMonth.year() : lastMonth.year() - 1;
    const minFY = currentFY - 5;
    const minDate = dayjs().year(minFY).month(3).startOf("month");
    const maxDate = today.endOf("month");
    return { minDate, maxDate, minFY, maxFY: currentFY };
  };

  const isYearAllDisabled = (year: number) => {
    const { minDate, maxDate } = getFinancialYearBounds();
    return months.every((_, idx) => {
      const current = dayjs().year(year).month(idx).startOf("month");
      if (current.isBefore(minDate)) return true;
      if (current.isAfter(maxDate)) return true;
      return false;
    });
  };

  const isMonthInRange = (
    current: dayjs.Dayjs,
    startMonth: dayjs.Dayjs | null,
    endMonth: dayjs.Dayjs | null
  ) => {
    return (
      !!startMonth &&
      !!endMonth &&
      current.isSameOrAfter(startMonth, "month") &&
      current.isSameOrBefore(endMonth, "month")
    );
  };

  const isMonthStart = (
    current: dayjs.Dayjs,
    startMonth: dayjs.Dayjs | null
  ) => {
    return !!startMonth && current.isSame(startMonth, "month");
  };

  const isMonthEnd = (current: dayjs.Dayjs, endMonth: dayjs.Dayjs | null) => {
    return !!endMonth && current.isSame(endMonth, "month");
  };

  const isMonthDisabled = (
    current: dayjs.Dayjs,
    minDate: dayjs.Dayjs,
    maxDate: dayjs.Dayjs
  ) => {
    if (current.isBefore(minDate)) return true;
    if (current.isAfter(maxDate)) return true;
    return false;
  };

  const dateRangeLabel = formatRange();
  const tooltipTitle = !open && dateRangeLabel ? dateRangeLabel : "";

  return (
    <div>
      <Typography variant="h6" sx={operationsDashboardStyles.dateRangeText}>
        Start Date - End Date
      </Typography>
      <Box
        onClick={handleOpen}
        tabIndex={0}
        sx={{
          ...operationsDashboardStyles.monthRangePickerBox,
          border: open ? "1px solid red" : "1px solid #ccc",
          color: !(startMonth && endMonth)
            ? colors.placeholder_grey
            : colors.grayish_blue,
          "&:hover": {
            border: `1px solid ${colors.grayish_blue}`,
          },
          "&:hover .clearIcon": { opacity: 1 },
        }}
      >
        <Tooltip
          title={tooltipTitle}
          disableHoverListener={!tooltipTitle}
          {...filterFieldTooltipProps}
        >
          <Box
            component="span"
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {dateRangeLabel}
          </Box>
        </Tooltip>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <ExpandMoreIcon
            sx={{
              ...operationsDashboardStyles.expandMoreIcon,
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
            }}
          />
        </Box>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={operationsDashboardStyles.popoverBox}>
          <Box sx={operationsDashboardStyles.yearNavBox}>
            <IconButton
              size="small"
              onClick={() => setYear(year - 1)}
              disabled={isYearAllDisabled(year - 1)}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography sx={operationsDashboardStyles.yearText}>
              {year}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setYear(year + 1)}
              disabled={isYearAllDisabled(year + 1)}
            >
              <ArrowForwardIcon />
            </IconButton>
          </Box>

          <Box sx={operationsDashboardStyles.monthsGrid}>
            {months.map((m, idx) => {
              const current = dayjs().year(year).month(idx).startOf("month");
              const { minDate, maxDate } = getFinancialYearBounds();
              const inRange = isMonthInRange(current, startMonth, endMonth);
              const start = isMonthStart(current, startMonth);
              const end = isMonthEnd(current, endMonth);
              const disabled = isMonthDisabled(current, minDate, maxDate);

              let bgColor = "white";
              if (inRange) bgColor = colors.range_bg;
              if (start || end) bgColor = colors.selection_bg;

              return (
                <Button
                  key={m}
                  onClick={() => handleMonthClick(idx)}
                  disabled={disabled}
                  sx={{
                    ...operationsDashboardStyles.monthButton,
                    "&:hover": {
                      ...(start || end
                        ? { backgroundColor: colors.selection_bg }
                        : { backgroundColor: colors.range_bg }),
                    },
                  }}
                  style={bgColor ? { backgroundColor: bgColor } : undefined}
                >
                  {m}
                </Button>
              );
            })}
          </Box>
        </Box>
      </Popover>
    </div>
  );
}
